import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import SizePickerDialog from "@/components/SizePickerDialog";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [sizingProduct, setSizingProduct] = useState(null);
  const { add } = useCart();

  const setCategory = (c) => {
    if (c === "All") setParams({});
    else setParams({ category: c });
  };

  const handleAdd = (product) => {
    if (product.sizes && product.sizes.length > 0) {
      setSizingProduct(product);
    } else {
      add(product, 1);
      toast.success(`${product.name} added to cart`);
    }
  };

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = category === "All" ? products : products.filter((p) => p.category === category);

  return (
    <div data-testid="shop-page">
      <div className="px-6 md:px-12 lg:px-16 pt-16 pb-8">
        <div className="text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-3">Official Store</div>
        <h1 className="font-sports uppercase tracking-tight leading-none text-5xl sm:text-6xl lg:text-7xl text-halo-navy">
          Shop the <span className="text-halo-red">Section</span>.
        </h1>
        <p className="mt-5 font-body text-halo-navy/70 max-w-2xl">
          Caps, tees, and stadium-ready gear. Every order helps keep the postgame show on the air.
        </p>
      </div>

      <div className="px-6 md:px-12 lg:px-16 pb-6 flex flex-wrap gap-2 border-b border-border">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`category-${c.toLowerCase()}`}
            onClick={() => setCategory(c)}
            className={`px-4 py-2 font-sports uppercase tracking-wider text-xs border-2 transition-colors ${
              category === c
                ? "bg-halo-navy border-halo-navy text-halo-cream"
                : "border-halo-navy/20 text-halo-navy hover:border-halo-navy"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`shop-product-${p.id}`}
               className="group bg-white border border-border hover:-translate-y-1 transition-transform duration-300 flex flex-col">
            <div className="aspect-square overflow-hidden bg-halo-cream">
              {p.image_position ? (
                <div
                  className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${p.image})`,
                    backgroundSize: "200% auto",
                    backgroundPosition: "left center",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#000",
                  }}
                  role="img"
                  aria-label={p.name}
                />
              ) : (
                <img src={p.image} alt={p.name} loading="lazy"
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              )}
            </div>
            <div className="p-5 flex flex-col gap-3 flex-1">
              <div className="flex-1">
                <div className="text-[10px] font-sports uppercase tracking-widest text-halo-red">{p.category}</div>
                <h3 className="font-sports uppercase tracking-tight text-lg leading-tight mt-1 text-halo-navy">{p.name}</h3>
                <p className="font-body text-xs text-halo-navy/60 mt-2 line-clamp-3">{p.description}</p>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="font-sports text-2xl text-halo-navy">${p.price.toFixed(2)}</span>
                <button
                  data-testid={`shop-add-${p.id}`}
                  onClick={() => handleAdd(p)}
                  className="inline-flex items-center gap-2 bg-halo-navy hover:bg-halo-red text-halo-cream font-sports uppercase tracking-wider text-xs px-4 py-2 transition-colors"
                >
                  <ShoppingBag className="w-3.5 h-3.5" strokeWidth={2.5} />
                  {p.sizes && p.sizes.length > 0 ? "Select" : "Add"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <SizePickerDialog
        product={sizingProduct}
        open={!!sizingProduct}
        onOpenChange={(v) => { if (!v) setSizingProduct(null); }}
      />
    </div>
  );
}
